const dt = new Intl.DateTimeFormat('pl-PL', { timeZone: 'Europe/Warsaw', dateStyle: 'short', timeStyle: 'short' });

export const formatDateTime = (iso: string | null | undefined) => (iso ? dt.format(new Date(iso)) : '—');

/** Kod pocztowy w trakcie pisania: same cyfry (najwyżej 5), myślnik dopisywany po dwóch — „44100” → „44-100”. */
export const formatPostalCode = (v: string) => {
  const d = v.replace(/\D/g, '').slice(0, 5);
  return d.length > 2 ? `${d.slice(0, 2)}-${d.slice(2)}` : d;
};

// ── dni (daty „RRRR-MM-DD”; z ISO z godziną bierzemy dzień w strefie Warszawy) ──

const dayFmt = new Intl.DateTimeFormat('sv-SE', { timeZone: 'Europe/Warsaw' });
/** „RRRR-MM-DD” z daty albo ISO z godziną (dzień w czasie polskim). */
const dayOnly = (iso: string) => (iso.length === 10 ? iso : dayFmt.format(new Date(iso)));
const noon = (iso: string) => new Date(`${dayOnly(iso)}T12:00:00Z`);

const longFmt = {
  wy: new Intl.DateTimeFormat('pl-PL', { timeZone: 'UTC', weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }),
  w: new Intl.DateTimeFormat('pl-PL', { timeZone: 'UTC', weekday: 'long', day: 'numeric', month: 'long' }),
  y: new Intl.DateTimeFormat('pl-PL', { timeZone: 'UTC', day: 'numeric', month: 'long', year: 'numeric' }),
  n: new Intl.DateTimeFormat('pl-PL', { timeZone: 'UTC', day: 'numeric', month: 'long' }),
};
/** Dzień słownie: „czwartek, 5 marca 2026” (opcjonalnie bez dnia tygodnia / bez roku). */
export function dayLong(iso: string | null | undefined, opts: { weekday?: boolean; year?: boolean } = {}): string {
  if (!iso) return '—';
  const { weekday = true, year = true } = opts;
  return longFmt[weekday ? (year ? 'wy' : 'w') : year ? 'y' : 'n'].format(noon(iso));
}

/** Dzień krótko: „5.03”. */
export const dayShort = (iso: string) => {
  const d = dayOnly(iso);
  return `${Number(d.slice(8, 10))}.${d.slice(5, 7)}`;
};

/** Zakres dni: „5.03 – 11.03.2026” (lata różne: „29.12.2025 – 4.01.2026”; jeden dzień: „5.03.2026”). */
export function dayRange(fromIso: string, toIso: string): string {
  const from = dayOnly(fromIso);
  const to = dayOnly(toIso);
  if (from === to) return `${dayShort(to)}.${to.slice(0, 4)}`;
  const left = from.slice(0, 4) === to.slice(0, 4) ? dayShort(from) : `${dayShort(from)}.${from.slice(0, 4)}`;
  return `${left} – ${dayShort(to)}.${to.slice(0, 4)}`;
}

const monthFmt = new Intl.DateTimeFormat('pl-PL', { timeZone: 'UTC', month: 'long', year: 'numeric' });
/** Miesiąc: „marzec 2026”. */
export const monthPl = (iso: string) => monthFmt.format(noon(iso));

const timeFmt = new Intl.DateTimeFormat('pl-PL', { timeZone: 'Europe/Warsaw', hour: '2-digit', minute: '2-digit' });
/** Godzina chwili ISO w czasie polskim: „06:05”. */
export const timeOf = (iso: string) => timeFmt.format(new Date(iso));

/** Godzina z liczb: timeHm(6) → „06:00”. */
export const timeHm = (h: number, m = 0) => `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;

// ── liczby ──

/** Procent po polsku, bez spacji: „8,5%”. Domyślnie do 1 miejsca po przecinku, bez zbędnych zer. */
export const pctPl = (n: number, digits = 1) =>
  `${new Intl.NumberFormat('pl-PL', { maximumFractionDigits: digits, useGrouping: false }).format(n)}%`;

/** Ilość po polsku: przecinek dziesiętny, bez zer na końcu — „2,5”, „10”. */
export const qtyPl = (n: number) => new Intl.NumberFormat('pl-PL', { maximumFractionDigits: 4, useGrouping: false }).format(n);

/** Odmiana liczebnika: plural(n, 'zadanie', 'zadania', 'zadań') — 1 zadanie, 2 zadania, 5 zadań, 22 zadania, 12 zadań. */
export function plural(n: number, one: string, few: string, many: string): string {
  const a = Math.abs(n);
  if (a === 1) return one;
  const u = a % 10;
  const t = a % 100;
  return u >= 2 && u <= 4 && !(t >= 12 && t <= 14) ? few : many;
}
